<template>
  <div class="c-bulk-card">
    <header class="c-bulk-card__header">
      <span class="c-bulk-card__icon">
        <app-icon name="download" />
      </span>
      <div>
        <h3 class="c-bulk-card__title">
          Export SEO rules
        </h3>
        <p class="c-bulk-card__subtitle">
          Download SEO rules for this website as a CSV file you can edit and import back.
        </p>
      </div>
    </header>

    <div class="c-bulk-card__body">
      <section class="c-bulk-card__section">
        <h4 class="c-bulk-card__section-title">
          What to export
        </h4>
        <div class="c-bulk-card__grid">
          <label class="c-bulk-card__field">
            <span>Language</span>
            <select
              v-model="selectedLanguageId"
              class="ui-select"
            >
              <option
                v-for="language in languages"
                :key="language.code"
                :value="language.id"
              >
                {{ language.name }}
              </option>
            </select>
          </label>

          <label class="c-bulk-card__field">
            <span>Category</span>
            <select
              v-model="selectedCategory"
              class="ui-select"
              @change="handleCategorySelect(selectedCategory)"
            >
              <option
                v-for="category in categories"
                :key="category.value"
                :value="category.value"
              >
                {{ category.label }}
              </option>
            </select>
          </label>

          <label
            v-if="displaySportPicker"
            class="c-bulk-card__field"
          >
            <span>Sport</span>
            <select
              v-model="selectedSportId"
              class="ui-select"
              @change="handleSportSelect(selectedSportId)"
            >
              <option
                v-for="sport in sports"
                :key="sport.name"
                :value="sport.id"
              >
                {{ sport.name }}
              </option>
            </select>
          </label>

          <label
            v-if="displayRegionPicker"
            class="c-bulk-card__field"
          >
            <span>Region</span>
            <select
              v-model="selectedRegionId"
              class="ui-select"
            >
              <option
                v-for="region in regions"
                :key="region.name"
                :value="region.id"
              >
                {{ region.name }}
              </option>
            </select>
          </label>
        </div>
      </section>

      <section class="c-bulk-card__section">
        <div class="c-bulk-card__section-head">
          <h4 class="c-bulk-card__section-title">
            Fields to include
          </h4>
          <button
            type="button"
            class="c-bulk-card__link"
            @click="toggleAllFields"
          >
            {{ allFieldsSelected ? 'Select none' : 'Select all' }}
          </button>
        </div>

        <div class="c-bulk-card__options">
          <div
            v-for="field in fieldOptions"
            :key="field.key"
            :class="['c-bulk-card__option', { 'is-checked': exportFields[field.key] }]"
            role="checkbox"
            tabindex="0"
            :aria-checked="String(exportFields[field.key])"
            @click="toggleField(field.key)"
            @keydown.space.prevent="toggleField(field.key)"
            @keydown.enter.prevent="toggleField(field.key)"
          >
            <!-- visual only: the whole card is the click target -->
            <md-checkbox
              :model="exportFields[field.key]"
              tabindex="-1"
              aria-hidden="true"
            />
            <span>
              <strong>{{ field.label }}</strong>
              <small>{{ field.hint }}</small>
            </span>
          </div>
        </div>
      </section>
    </div>

    <footer class="c-bulk-card__footer">
      <span class="c-bulk-card__summary">
        {{ selectedFieldsCount }} {{ selectedFieldsCount === 1 ? 'field' : 'fields' }}
        · {{ selectedLanguageName }}
        · {{ selectedCategoryLabel }}
      </span>
      <button
        type="button"
        :class="['ui-btn ui-btn--primary', { 'ui-btn--loading': isSubmitting }]"
        :disabled="isSubmitting || !selectedFieldsCount"
        @click="handleExportSeo"
      >
        <app-icon name="download" />
        Export CSV
      </button>
    </footer>
  </div>
</template>

<script>
import { seoRulesService } from '@/services/seo-rules'
import AppIcon from '@/shared/app-icon/AppIcon'
import { store } from '@/store/store'
import moment from 'moment'

export default {
  name: "ExportSeoRulesComponent",

  components: {
    AppIcon,
  },

  props: {
    website: {
      type: Object,
      default: null,
    },

    languages: {
      type: Array,
      required: true
    },

    defaultLanguageId: {
      type: Number,
      required: false,
      default: null
    },
  },

  data() {
    return {
      selectedLanguageId: this.languages[0].id,
      selectedCategory: 'all',
      selectedSportId: -1,
      selectedRegionId: -1,
      categories: [
        { label: 'All', value: 'all' },
        { label: 'Sports', value: 'sport' },
        { label: 'Regions', value: 'region' },
        { label: 'Leagues', value: 'league' },
        { label: 'Pages', value: 'page' },
        { label: 'Casino Pages', value: 'casino-category' },
        { label: 'Templates', value: 'template' },
      ],
      exportFields: {
        metaTitle: true,
        metaDescription: true,
        metaKeywords: true,
        pageTitle: true,
        content: true
      },
      fieldOptions: [
        { key: 'metaTitle', label: 'Meta title', hint: 'Search result title' },
        { key: 'metaDescription', label: 'Meta description', hint: 'Search result summary' },
        { key: 'metaKeywords', label: 'Meta keywords', hint: 'Comma separated keywords' },
        { key: 'pageTitle', label: 'H1 page title', hint: 'Main heading on the page' },
        { key: 'content', label: 'SEO content', hint: 'Rich text shown on the page' },
      ],
      sports: [],
      regions: [],
      isSubmitting: false,
    };
  },

  computed: {
    selectedFieldsCount() {
      return Object.values(this.exportFields).filter(Boolean).length;
    },

    allFieldsSelected() {
      return this.selectedFieldsCount === this.fieldOptions.length;
    },

    selectedLanguageName() {
      const language = this.languages.find(({ id }) => id === this.selectedLanguageId);

      return language ? language.name : '';
    },

    selectedCategoryLabel() {
      const category = this.categories.find(({ value }) => value === this.selectedCategory);

      return category ? `${category.label === 'All' ? 'All categories' : category.label}` : '';
    },

    displaySportPicker() {
      // TODO: When we get additional info how exactly should sport and region exports work, uncomment this and implement sport and region exporting on API
      // return ['sport', 'region', 'league'].includes(this.selectedCategory);
      return false;
    },

    displayRegionPicker() {
      // TODO: When we get additional info how exactly should sport and region exports work, uncomment this and implement sport and region exporting on API
      // return ['region', 'league'].includes(this.selectedCategory) && this.selectedSportId >= 0;
      return false;
    }
  },

  methods: {
    toggleField(key) {
      this.exportFields[key] = !this.exportFields[key];
    },

    toggleAllFields() {
      const value = !this.allFieldsSelected;

      Object.keys(this.exportFields).forEach(key => {
        this.exportFields[key] = value;
      });
    },

    async handleCategorySelect() {
      // TODO: When we get additional info how exactly should sport and region exports work, uncomment this and implement sport and region exporting on API
      // async handleCategorySelect(category) {
      // if (['sport', 'region', 'league'].includes(category)) {
      //   await this.fetchSports();
      // }
      //
      // this.selectedSportId = -1;
      // this.selectedRegionId = -1;
    },

    handleSportSelect(sportId) {
      this.selectedRegionId = -1;

      if (!this.displayRegionPicker) {
        return;
      }

      this.fetchRegions(sportId)
    },

    async fetchSports() {
      if (this.sports.length) {
        return;
      }

      this.sports = [{ id: -1, name: 'All' }];
      const sportsResponse = await seoRulesService.getSports();
      this.sports.push(...sportsResponse.data);
    },

    async fetchRegions(sportId) {
      this.regions = [{ id: -1, name: 'All' }];
      const regionsResponse = await seoRulesService.getRegions(sportId);
      this.regions.push(...regionsResponse.data);
    },

    async handleExportSeo() {
      this.isSubmitting = true;
      try {
        const response = await seoRulesService.exportSeo(
            this.website.id,
            this.selectedLanguageId,
            this.exportFields,
            this.selectedCategory,
            this.selectedSportId,
            this.selectedRegionId
        )

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', this.getExportFileName())
        document.body.appendChild(link)
        link.click()
      } catch(e) {
        store.dispatch('response/setGlobalError', 'There are no available SEO rules for given criteria!');
      }

      this.isSubmitting = false;
    },

    getExportFileName() {
      const selectedLanguage = this.languages.find(language => language.id === this.selectedLanguageId);
      let fileName = `seo-export-${this.website.name}-${selectedLanguage.code}`;

      if (this.selectedCategory !== 'all') {
        fileName += `-${this.selectedCategory}`
      }

      if (this.selectedSportId >= 0) {
        const selectedSport = this.sports.find(sport => sport.id === this.selectedSportId);
        fileName += `-${selectedSport.name}`
      }

      if (this.selectedRegionId >= 0) {
        const selectedRegion = this.regions.find(region => region.id === this.selectedRegionId);
        fileName += `-${selectedRegion.name}`
      }

      fileName += '-' + moment().format('YYYYMMDDHHmmss') + '-v4.csv';
      fileName = fileName.replace(' ', '-')


      return fileName.toLowerCase();
    }
  }
}
</script>

<style lang="scss" scoped>
@import './bulk-card';
</style>
